import type { SirCreditTier } from "./types";

/**
 * SIR (Self-Insured Retention) credit lookup.
 *
 * Each tier maps a retention dollar amount → a credit fraction
 * applied multiplicatively to the maturity-adjusted premium:
 *
 *     SIR Adj. Premium = Mat. Adj. Premium × (1 − sirCredit)
 *
 * Higher retentions yield larger credits because the insured covers
 * more risk before the carrier pays out, so the carrier's exposure
 * (and the premium it quotes) drops.
 *
 * The tier table itself is no longer hard-coded here — it comes from
 * the real `GET /v2/credit-factors/sir` endpoint. Callers pass the
 * resolved table in via `FacilityCalcRefs.sirTable`.
 *
 * Exact-match only — mirrors the spreadsheet's XLOOKUP behaviour. No
 * nearest-tier fallback because the actuary maintains discrete tiers
 * and a retention amount between tiers is almost certainly a data
 * entry mistake; surfacing `null` lets the UI flag it instead of
 * silently quoting an interpolated number.
 *
 * A retention of exactly `0` is a valid selection — the carrier's table
 * carries a `$0 → 0%` tier — so it resolves to a real 0% credit (and a
 * SIR Adj. Premium equal to the Mat. Adj. Premium) rather than `null`.
 * Only a blank/unset retention (passed in as `NaN`) or a negative
 * amount yields `null`, leaving the UI to render a dash.
 */
export function lookupSirCredit(retention: number, table: SirCreditTier[]): number | null {
    if (!Number.isFinite(retention) || retention < 0) return null;
    const tier = table.find((t) => t.retention === retention);
    return tier ? tier.credit : null;
}
